import * as React from 'react';
import {AnnualPolicyPremiumTypeEnum} from "@falcon/services";
import {getAmountToDisplay} from "../../../../CommonFunctions";
import {StorageSuccessModal} from "../../StorageSuccessModal";

interface IProps {
    factors: any;
    payFullPrice: boolean;
    cardEnding: string;
    renewalPrice: any;
    instalmentsPaidOff: boolean;
    isPaymentsDone: boolean;
}

export class ChangeAddressStorageSuccessModal extends React.Component<IProps> {

    public render(): React.ReactNode {
        const factors: any = this.props.factors;
        const refund: boolean = factors?.returnPremium > 0 && factors?.mtaAndDebtCombinedAdditionalAmount === 0;
        const payment: boolean = factors?.additionalPremium > 0 || factors?.mtaAndDebtCombinedAdditionalAmount > 0;

        if (this.props.factors.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL) {
            return this.renderAnnualModals(factors, refund, payment);
        } else {
            return this.renderMonthlyModals(factors, refund, payment);
        }
    }

    private renderAnnualModals(factors: any, refund: boolean, payment: boolean): React.ReactNode {
        let title: React.ReactNode;
        let content: React.ReactNode;

        if (refund) {
            title = "Your Refund is on the way";
            content =
                <>
                    <p>
                        We’ve saved your changes, and we’ll refund you{' '}
                        <span className="font-weight-bold"> {getAmountToDisplay(factors.returnPremium)}.</span>{' '}
                        Please allow two days for it to arrive.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else if (payment) {
            title = "Payment was successful";
            content =
                <>
                    <p>
                        {getAmountToDisplay(this.getAPAmount(factors))} has been taken out from your card ending in
                        <span className="font-weight-bold"> {this.props.cardEnding}</span>{' '}
                        to change the address on your home policy
                        {(factors.mtaAndDebtCombinedAdditionalAmount > 0) && ' and clear the outstanding balance due'}.
                        <br/>
                        Please allow a couple of minutes for the payment to be updated in your account.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else {
            title = "All done!";
            content =
                <>
                    <p>
                        We’ve saved your changes, and there’s no extra cost for changing your address.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        }

        return <StorageSuccessModal title={title} explanationWording={content} />
    }

    private renderMonthlyModals(factors: any, refund: boolean, payment: boolean): React.ReactNode {
        let title: React.ReactNode;
        let content: React.ReactNode;
        const refundAmount = this.getRefundAmount(factors);

        if (refund && !this.props.instalmentsPaidOff) {
            title = "Your payment plan has been updated";
            content =
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountToDisplay(refundAmount)}</span>{' '}
                        has been reduced from your monthly payments.
                        {!this.props.isPaymentsDone && <span> Your future monthly payments will be{' '}
                            <span className="font-weight-bold">{getAmountToDisplay(factors.monthlyPremium)}</span>.
                        </span>}
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else if (refund && this.props.instalmentsPaidOff) {
            title = "All done";
            content =
                <>
                    <p>
                        Your changes have been made and the money will be refunded to the bank account/card that
                        your monthly payments were being debited from within the next 3-5 working days.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else if (payment && !this.props.payFullPrice) {
            // prototype doesn't have wording about future monthly payments, probably a mistake?
            title = "All done!";
            content =
                <>
                    <p>
                        <span className="font-weight-bold">{getAmountToDisplay(factors.monthlyPaymentSpreadWithInterestAmount || factors.additionalPremium)}</span>{' '}
                        has been added to your monthly payments for changing the address on your home policy.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else if (payment && this.props.payFullPrice) {
            title = "Payment was successful";
            content =
                <>
                    <p>
                        {getAmountToDisplay(factors.additionalPremium)} has been taken out from your card ending in
                        <span className="font-weight-bold"> {this.props.cardEnding}</span>{' '}
                        to change the address on your home policy.<br/>
                        Please allow a couple of minutes for the payment to be updated in your account.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        } else {
            title = "All done!";
            content =
                <>
                    <p>
                        We’ve saved your changes, and there’s no extra cost for changing your address.
                    </p>
                    {this.renderNextYearRenewalPrice()}
                </>
        }

        return <StorageSuccessModal title={title} explanationWording={content} />
    }

    private getRefundAmount(factors: any) {
        return factors.mtaAndDebtCombinedReturnAmount > 0 ? factors.mtaAndDebtCombinedReturnAmount : factors.returnPremium;
    }

    private renderNextYearRenewalPrice(): React.ReactNode {
        if (this.props.renewalPrice) {
            return (
                <p className="font-weight-bold">
                    Your next year’s renewal price will be {getAmountToDisplay(this.props.renewalPrice)}
                </p>
            )
        } else {
            return <></>
        }
    }

    private getAPAmount(factors: any) {
        if (factors.mtaAndDebtCombinedAdditionalAmount > 0) {
            return factors.mtaAndDebtCombinedAdditionalAmount;
        }
        return factors.additionalPremium;
    }
}
